import React, { useMemo, useRef } from 'react';

import {
  HStack, Grid, Stack, Text, Flex, StackProps, Input,
} from '@chakra-ui/react';
import { useMutation } from 'react-query';

import { updateUserCompany } from 'api/company';
import { uploadFile } from 'api/s3';
import { Button, Icon } from 'components';
import Avatar from 'components/avatar/default';
import { AccessTypes, MethodTypes } from 'enums/types';
import { policy } from 'polices';
import useCompany from 'stores/useCompany';
import { handleObjectString } from 'utils/parsers';

import { fields } from './constants';

function InfoRow({
  label = '', name = '', data, span = false, omitable = false,
}) {
  if (span) {
    return <span />;
  }
  if (omitable && !handleObjectString(data, name)) {
    return <span />;
  }
  return (
    <Stack spacing={1}>
      <Text variant="smallSemiBold">{label}</Text>
      <Text
        variant="smallRegular"
        color="gray.600">{handleObjectString(data, name) || 'N/A'}
      </Text>
    </Stack>
  );
}

function UpdateAvatar() {
  const { refetch } = useCompany();

  const ref = useRef(null);

  const {
    mutate,
    isLoading,
  } = useMutation(({ url }: any) => updateUserCompany({
    data: { brandUrl: url },
  }),
  {
    onSuccess: refetch,
  });

  const {
    mutate: mutateS3,
    isLoading: isLoadingS3,
  } = useMutation((file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return uploadFile(formData);
  }, {
    onSuccess: ({ data }) => mutate(data),
  });

  return (
    <>
      <Input
        type="file"
        multiple={false}
        ref={ref}
        onChange={(event) => mutateS3(event.target.files[0])}
        hidden
        name="brandUrl"
      />
      <Button
        mt={2}
        size="sm"
        isLoading={isLoadingS3 || isLoading}
        onClick={() => ref?.current?.click()}
        variant="secondary.teal">
        <Icon
          size="xs"
          mr={1}
          name="camera"
          family="far"
        />
        <Text variant="xSmallBold">
          Editar avatar
        </Text>
      </Button>
      <Button
        size="sm"
        isLoading={isLoadingS3 || isLoading}
        onClick={() => mutate({ url: 'null' })}
        variant="ghost.teal">
        <Text
          color="system.error.default"
          variant="xSmallRegular">
          Remover avatar
        </Text>
      </Button>
    </>
  );
}

function AvatarField({ data }) {
  const policies = policy('company');
  const isEditable = useMemo(() => policies(MethodTypes.Update) === AccessTypes.Allow, [policies]);

  return (
    <Flex
      alignItems="center"
      minWidth={130}
      flexDir="column"
      mr={10}>
      <Avatar
        h={100}
        w={100}
        name={data.name}
        src={data.brandUrl}
      />
      {isEditable && <UpdateAvatar />}
    </Flex>
  );
}

export default function Info({ data, ...rest }: StackProps & {data: any}) {
  if (!data) {
    return null;
  }
  return (
    <HStack
      maxW="1024px"
      bg="white.default"
      borderRadius={8}
      p={6}
      {...rest}>
      <Flex
        height="100%">
        <AvatarField data={data} />
      </Flex>
      <Grid
        width="100%"
        gap={6}
        templateColumns="repeat(2,1fr)">
        {fields.map((field) => (
          <InfoRow
            key={field.name}
            {...field}
            data={data}
          />
        ))}
      </Grid>
    </HStack>
  );
}
